import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../clients/supabaseClient.tsx';
import logo from '../assets/logo.png';

type VerifyState = 'verifying' | 'verified' | 'error';

export default function AccountDeletionVerify() {
    const [searchParams] = useSearchParams();
    const [state, setState] = useState<VerifyState>('verifying');
    const [message, setMessage] = useState('Verifying your deletion request…');

    useEffect(() => {
        const verifyRequest = async () => {
            const token = searchParams.get('token');

            if (!token) {
                setState('error');
                setMessage('This verification link is missing a token.');
                return;
            }

            try {
                const { data, error } = await supabase.functions.invoke('verify-account-deletion', {
                    body: {
                        token,
                    },
                });

                if (error) {
                    throw error;
                }

                setState('verified');
                setMessage(
                    data?.message ??
                    'Your deletion request has been verified. Our deletion team has been notified and will process your request.'
                );
            } catch (error) {
                console.error('Account deletion verification failed:', error);
                setState('error');
                setMessage('This verification link is invalid, expired, or has already been used.');
            }
        };

        verifyRequest();
    }, [searchParams]);

    const title =
        state === 'verified'
            ? 'Request verified'
            : state === 'error'
                ? 'Verification failed'
                : 'Verifying request';

    const statusIcon =
        state === 'verified' ? '✓' : state === 'error' ? '!' : '…';

    return (
        <main className="min-h-screen bg-white px-4 py-10 text-gray-900 sm:py-14 flex items-center">
            <section className="mx-auto grid max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-white shadow-2xl lg:grid-cols-[0.9fr_1.1fr]">
                <aside className="relative hidden bg-[#161d2b] p-10 text-white lg:block">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(230,24,108,0.38),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.12),transparent_32%)]" />

                    <div className="relative z-10 flex h-full flex-col justify-between">
                        <div>
                            <div className="mb-10 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-3 shadow-lg shadow-black/20">
                                <img src={logo} alt="Save Delivery" className="h-full w-full object-contain" />
                            </div>

                            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-pink-200">
                                Save Delivery
                            </p>

                            <h1 className="text-4xl font-bold tracking-tight">
                                Account deletion verification
                            </h1>

                            <p className="mt-5 max-w-sm text-sm leading-7 text-slate-200">
                                We are confirming ownership of the account before notifying our
                                deletion team.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                            <p className="text-sm font-semibold text-white">Privacy protected</p>
                            <p className="mt-2 text-sm leading-6 text-slate-200">
                                Deletion requests are only sent to our team after the verification link
                                from your registered email has been confirmed.
                            </p>
                        </div>
                    </div>
                </aside>

                <div className="flex min-h-[520px] flex-col justify-center bg-white p-6 text-center sm:p-8 lg:p-12">
                    <div className="mb-8 lg:hidden">
                        <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-pink-100 bg-pink-50 p-2 shadow-sm">
                            <img src={logo} alt="Save Delivery" className="h-full w-full object-contain" />
                        </div>

                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#e6186c]">
                            Save Delivery
                        </p>
                    </div>

                    <div
                        className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full text-3xl font-bold ring-8 ${
                            state === 'verified'
                                ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                : state === 'error'
                                    ? 'bg-rose-50 text-rose-700 ring-rose-100'
                                    : 'bg-pink-50 text-[#e6186c] ring-pink-100'
                        }`}
                    >
                        {statusIcon}
                    </div>

                    <p className="mb-2 text-sm font-semibold text-[#e6186c]">
                        Account & data deletion
                    </p>

                    <h2 className="text-3xl font-bold tracking-tight text-[#161d2b] sm:text-4xl">
                        {title}
                    </h2>

                    <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-gray-600">
                        {message}
                    </p>

                    {state === 'verifying' && (
                        <div className="mx-auto mt-8 flex items-center justify-center gap-2 rounded-full bg-pink-50 px-4 py-2 text-sm font-medium text-[#e6186c]">
                            <span className="h-2 w-2 animate-ping rounded-full bg-[#e6186c]" />
                            Checking verification link
                        </div>
                    )}

                    {state !== 'verifying' && (
                        <div className="mt-8">
                            <Link
                                to="/account-deletion"
                                className="inline-flex h-12 items-center justify-center rounded-xl bg-[#e6186c] px-5 text-sm font-semibold text-white shadow-sm shadow-pink-200 transition hover:bg-[#d01560]"
                            >
                                Back to account deletion
                            </Link>
                        </div>
                    )}

                    <p className="mx-auto mt-8 max-w-lg border-t border-stone-200 pt-5 text-xs leading-5 text-gray-500">
                        If you did not request account deletion, no further action is required. You can
                        contact support if you believe this request was made in error.
                    </p>
                </div>
            </section>
        </main>
    );
}